<template>
  <div id="backtester-results">
    <v-card v-if="!results">
      <v-card-text class="text-body-2"> Backtest not run yet. </v-card-text>
    </v-card>

    <v-container v-else fluid pa-0>
      <!-- Buttons: Excel, Start/Stop tracking -->
      <v-row>
        <v-col cols="12">
          <v-btn
            :small="$vuetify.display.xs"
            :href="`/generated_backtesters/${results.excel_file}`"
            target="_blank"
            color="primary"
            class="ml-0"
          >
            Download Backtest Data to Excel
          </v-btn>

          <!-- Start tracking -->
          <v-dialog v-if="user" v-model="trackingStartDialog" :max-width="350" persistent>
            <template #activator="{ props }">
              <v-btn :small="$vuetify.display.xs" color="primary" class="ml-0" v-bind="props">
                <span v-if="criteria.backtest_tracking_id">Copy Backtest</span>
                <span v-else>Start Tracking</span>
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                <v-toolbar-title>
                  <span v-if="criteria.backtest_tracking_id">Copy Backtest</span>
                  <span v-else>Start Tracking</span>
                </v-toolbar-title>
              </v-card-title>
              <v-card-text>
                <v-form
                  ref="trackingForm"
                  v-model="validTrackingDescription"
                  lazy-validation
                  @submit.prevent="trackingStart()"
                >
                  <v-text-field
                    ref="backtestDescriptionField"
                    v-model="criteria.backtest_tracking_description"
                    :rules="[v => !!v || 'Backtest description is required']"
                    label="Backtest description"
                    @keyup.enter="trackingStart()"
                  />
                </v-form>
              </v-card-text>
              <v-card-actions class="px-4">
                <v-spacer />
                <v-btn text @click="cancelTrackingStart()"> Cancel </v-btn>
                <v-btn
                  :loading="loadingTracking"
                  :disabled="loadingTracking || !validTrackingDescription"
                  depressed
                  color="primary"
                  @click="trackingStart()"
                >
                  <span v-if="criteria.backtest_tracking_id">Copy Backtest</span>
                  <span v-else>Start Tracking</span>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-btn
            v-if="1 == 2 && user && criteria.backtest_tracking_id"
            :small="$vuetify.display.xs"
            color="primary"
            class="ml-0"
            @click="UpdateBacktestTracking(criteria.backtest_tracking_id)"
          >
            <span>Update Alert</span>
          </v-btn>
          <!-- Stop tracking -->
          <v-dialog
            v-if="user && criteria.backtest_tracking_id"
            v-model="trackingStopDialog"
            :max-width="350"
            persistent
          >
            <template #activator="{ props }">
              <v-btn :small="$vuetify.display.xs" color="primary" class="ml-0" v-bind="props">
                Stop tracking
              </v-btn>
            </template>

            <v-card>
              <v-card-title>
                <v-toolbar-title> Stop tracking this backtest </v-toolbar-title>
              </v-card-title>
              <v-card-text class="mb-2"> Delete this Backtest from your records? </v-card-text>
              <v-card-actions class="px-4">
                <v-spacer />
                <v-btn text @click="trackingStopDialog = false"> Cancel </v-btn>
                <v-btn
                  :loading="loadingTracking"
                  :disabled="loadingTracking"
                  depressed
                  color="primary"
                  @click="trackingStop()"
                >
                  Stop tracking
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>

      <!-- Timeframe, Moving averages, Index selection, Backtest chart -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <!-- Timeframe, Index selection, Moving averages -->
            <v-card-text>
              <v-container fluid pa-0>
                <v-row align="center">
                  <!-- Indices & Moving averages -->
                  <v-col cols="12" xl="8" order-xl="2">
                    <v-row align="center" justify="end">
                      <!-- Index selection -->
                      <v-col order-xl="2">
                        <v-row align="center">
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="chart.xAxis"
                              :items="chartIntervalItems"
                              label="Interval"
                              item-title="description"
                              return-object
                              hide-details
                              class="ma-0"
                              style="max-width: 150px"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="chart.comparisonIndex"
                              :items="indices"
                              label="Comparison index"
                              item-title="description"
                              return-object
                              hide-details
                              class="ma-0"
                              style="max-width: 300px"
                              @update:model-value="refresh()"
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Moving averages -->
                      <v-col :mr-4="$vuetify.display.xl" class="shrink" order-xl="1">
                        <v-btn
                          variant="outlined"
                          small
                          class="ma-0"
                          @click="movingAveragesDialog = true"
                        >
                          Configure moving averages
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row align="center">
                  <!-- Timeframe -->
                  <v-col cols="12" xl="4" order-xl="1">
                    <v-btn-toggle
                      v-model="timeframe"
                      :class="{ 'justify-space-between': $vuetify.display.xs }"
                      mandatory
                      class="elevation-0 transparent"
                      @update:model-value="refresh()"
                    >
                      <v-btn
                        v-for="item in timeframeItems"
                        :key="item.value"
                        :value="item"
                        :class="{ 'px-3': $vuetify.display.smAndUp }"
                        large
                        text
                      >
                        {{ item.description }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <!-- Subscription call to action -->
            <v-card-text v-if="!isSubscriber" class="pa-6 bg-grey-darken-3 text-white">
              <h5 class="text-h5 mb-2">Want to backtest using all available 13F history?</h5>
              <div>
                As a non-subscriber you are limited to the past two years of filing data.
                <a style="color: #fff033" href="/pricing" target="_blank">Subscribe now</a> and
                backtest using 13F holdings data as far back as 2001.
              </div>
            </v-card-text>

            <!-- Backtest Chart -->
            <v-card-text>
              <div id="backtest_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Backtest Table -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-table"
                :headers="backtestTableHeaders"
                :items="backtestTableItems"
                hide-default-footer
              >
                <template #item="{ item }">
                  <tr>
                    <td>{{ item['name'] }}</td>
                    <td :class="[item['YTD'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['YTD']) }}
                    </td>
                    <td :class="[item['1Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['1Y']) }}
                    </td>
                    <td :class="[item['2Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['2Y']) }}
                    </td>
                    <td :class="[item['3Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['3Y']) }}
                    </td>
                    <td :class="[item['5Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['5Y']) }}
                    </td>
                    <td :class="[item['7Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['7Y']) }}
                    </td>
                    <td :class="[item['10Y'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['10Y']) }}
                    </td>
                    <td :class="[item['Total Return'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['Total Return']) }}
                    </td>
                    <td :class="[item['Annualized'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['Annualized']) }}
                    </td>
                    <td
                      :class="[
                        item['performance_three_year_annualized'] > 0 ? 'text-green' : 'text-red'
                      ]"
                    >
                      {{ percent(item['performance_three_year_annualized']) }}
                    </td>
                    <td
                      :class="[
                        item['performance_five_year_annualized'] > 0 ? 'text-green' : 'text-red'
                      ]"
                    >
                      {{ percent(item['performance_five_year_annualized']) }}
                    </td>
                    <td
                      :class="[
                        item['performance_ten_year_annualized'] > 0 ? 'text-green' : 'text-red'
                      ]"
                    >
                      {{ percent(item['performance_ten_year_annualized']) }}
                    </td>
                    <td :class="[item['Std Dev'] > 0 ? 'text-green' : 'text-red']">
                      {{ percent(item['Std Dev']) }}
                    </td>
                    <td>{{ percent(item['Sortino']) }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Current Holdings Rebalanced -->
      <v-row>
        <v-col cols="12">
          <v-card>
            <v-card-text class="pa-6 bg-grey-darken-3 text-white">
              <h2 class="text-h5 mb-2 d-inline-flex align-center" style="color: #fff">
                <img
                  src="https://d27mjrcvcy56qq.cloudfront.net/images/ico_graph.png"
                  class="mr-2"
                />
                Current Holdings Rebalanced on {{ results.ending_period.start_date }}
              </h2>
              <div class="mb-4">
                Click the "Download Backtest Data to Excel" button above to see all stock activity
              </div>
              <v-alert type="info">
                Stock prices used are the "total returns" stock price and will include dividends,
                spinoffs, splits, and other adjustments. Because of this, stock prices shown here
                will likely not match a stock's actual closing stock price on the same date.
              </v-alert>

              <v-dialog v-model="CreateWatchlistDialog" width="500">
                <template #activator="{ props }">
                  <v-btn
                    :small="$vuetify.display.xs"
                    target="_blank"
                    v-bind="props"
                    color="primary"
                    class="ml-0"
                  >
                    Create a Watchlist From Ending Portfolio
                  </v-btn>
                </template>

                <v-card>
                  <v-card-title class="text-h5 bg-grey-lighten-2">
                    Create Stock Watchlist
                  </v-card-title>

                  <v-card-text>
                    <v-alert density="compact" type="info">
                      The number of stocks that will be added to the watchlist will be limited by
                      your subscription status. Your current limit is {{ limit }} per watchlist
                    </v-alert>

                    <v-alert v-if="stocksTableItems.length == 0" density="compact" type="warning">
                      No stocks were matched
                    </v-alert>
                    <v-text-field
                      v-model="newWatchlist.description"
                      hide-details
                      clearable
                      autofocus
                      autocorrect="off"
                      spellcheck="false"
                      class="pt-0"
                      style="min-width: 300px"
                      @keypress.enter="addWatchlist(stocksTableItems)"
                    />
                  </v-card-text>

                  <v-divider></v-divider>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      @click="CreateWatchlistDialog = false"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      :disabled="
                        newWatchlist.description.trim().length == 0 || stocksTableItems.length == 0
                      "
                      @click="addWatchlist(stocksTableItems)"
                    >
                      Create Watchlist
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Stocks Table & Ending Sector Allocation chart -->
      <v-row mb-2>
        <!-- Stocks Table -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <v-data-table
                id="backtest-stocks-table"
                :headers="stocksTableHeaders"
                :items="stocksTableItems"
                items-per-page="-1"
                hide-default-footer
              >
                <template #item="{ item }">
                  <!-- Default row -->
                  <tr v-if="item.rowType === 'default'">
                    <td>
                      <a
                        :href="`/stock/${item.stock_permalink}`"
                        :title="item.stock_name"
                        class="font-weight-bold"
                        target="_blank"
                        v-text="item.ticker"
                      />
                      {{ item.stock_name }}
                    </td>
                    <td>
                      {{ !item.shares ? 'Sold All' : percent(item.percent_of_portfolio * 100.0) }}
                    </td>
                    <td :class="[GetTransactionTotalReturn(item) > 0 ? 'text-green' : 'text-red']">
                      {{
                        item.is_hedge
                          ? currency(starting_record.hedging_gain)
                          : percent(GetTransactionTotalReturn(item))
                      }}
                    </td>
                    <td>{{ item.original_purchase_date }}</td>
                  </tr>

                  <!-- Exception: results.ending_period.hedge -->
                  <tr v-if="item.rowType === 'endingPeriodHedge'">
                    <td>{{ results.ending_period.hedge.stock_name }}</td>
                    <td />
                    <td :class="[starting_record.hedging_gain > 0 ? 'text-green' : 'text-red']">
                      {{ percent(GetTransactionTotalReturn(results.ending_period.hedge)) }}
                    </td>
                    <td>{{ results.ending_period.hedge.original_purchase_date }}</td>
                  </tr>

                  <!-- Exception: ending_hedged_transaction && !ending_hedged_transaction.is_sold -->
                  <tr v-if="item.rowType === 'endingHedgedTransaction'">
                    <td>*** Currently using {{ ending_hedged_transaction.stock_name }} ***</td>
                    <td />
                    <td :class="[starting_record.hedging_gain > 0 ? 'text-green' : 'text-red']">
                      {{ percent(GetTransactionTotalReturn(ending_hedged_transaction)) }}
                    </td>
                    <td>{{ ending_hedged_transaction.original_purchase_date }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Ending Sector Allocation chart -->
        <v-col cols="12" xl="6">
          <v-card>
            <v-card-text>
              <div id="backtest_sector_chart" />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Moving Averages dialog -->
    <v-dialog v-model="movingAveragesDialog" persistent width="600px">
      <v-card>
        <v-card-title class="text-h6 primary text-white"> Configure Moving Averages </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="maToAdd"
            :rules="[
              value =>
                !moving_averages.some(ma => ma.months === value) ||
                `${value} month moving average already added`
            ]"
            type="number"
            placeholder="Add an X month moving average"
            solo
            class="mb-2"
            @keydown.enter="AddMovingAverage()"
          >
            <template #prepend-inner>
              <v-icon variant="plain" color="success" @click="AddMovingAverage()">
                {{ mdiPlus }}
              </v-icon>
            </template>
          </v-text-field>
          <v-card v-if="moving_averages.length > 0">
            <v-list class="py-0">
              <template v-for="(ma, i) in moving_averages" :key="`${i}-${ma.description}`">
                <v-divider v-if="i !== 0" />

                <v-list-item>
                  <v-list-item-title>
                    {{ ma.description }}
                  </v-list-item-title>

                  <template #append>
                    <v-btn variant="plain" :icon="mdiDelete" @click="DeleteMovingAverage(ma)" />
                  </template>
                </v-list-item>
              </template>
            </v-list>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" text @click="CloseMovingAverageDialog()"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Modules
import api from '@/store/api'
import Highcharts from 'highcharts'
import last from 'lodash/last' // returns the last element of a given array
import { mapStoreState, mapFields } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'

// Mixins
import FormattersMixin from '@/mixins/formatters'
import { mdiDelete, mdiPlus } from '@mdi/js'

export default {
  mixins: [FormattersMixin],

  data: () => ({
    mdiDelete,
    mdiPlus,
    movingAveragesDialog: false,
    CreateWatchlistDialog: false,
    newWatchlist: { id: null, description: 'New Watchlist', visibility_type_id: 0, stocks: [] },
    trackingStartDialog: false,
    trackingStopDialog: false,
    validTrackingDescription: false,
    loadingTracking: false,
    results: null,
    timeframe: null,
    timeframeItems: [
      {
        description: 'YTD',
        value: 1,
        requires_subscription: false,
        time: 1
      },
      {
        description: '1y',
        value: 2,
        requires_subscription: false,
        time: 12
      },
      {
        description: '2y',
        value: 8,
        requires_subscription: false,
        time: 24
      },
      {
        description: '3y',
        value: 3,
        requires_subscription: true,
        time: 36
      },
      {
        description: '5y',
        value: 4,
        requires_subscription: true,
        time: 60
      },
      {
        description: '7y',
        value: 5,
        requires_subscription: true,
        time: 84
      },
      {
        description: '10y',
        value: 6,
        requires_subscription: true,
        time: 120
      },
      {
        description: 'Max',
        value: 0,
        requires_subscription: true,
        time: 0
      }
    ],
    chart: {
      xAxis: null,
      comparisonIndex: null
    },
    chartIntervalItems: [
      {
        value: 'yearly_entries',
        description: 'Yearly'
      },
      {
        value: 'quarterly_entries',
        description: 'Quarterly'
      },
      {
        value: 'monthly_entries',
        description: 'Monthly'
      }
    ],
    chartData: [],
    indices: [],
    maToAdd: null,
    moving_averages: [],
    starting_record: null,
    hedged_matrix: null,
    ending_hedged_transaction: null,
    is_compound_chart: true,
    backtestTableHeaders: [
      { title: '', key: 'name', sortable: false },
      { title: 'YTD', key: 'YTD', sortable: false },
      { title: '1Y', key: '1Y', sortable: false },
      { title: '2Y', key: '2Y', sortable: false },
      { title: '3Y', key: '3Y', sortable: false },
      { title: '5Y', key: '5Y', sortable: false },
      { title: '7Y', key: '7Y', sortable: false },
      { title: '10Y', key: '10Y', sortable: false },
      { title: 'Total Return', key: 'Total Return', sortable: false },
      { title: 'Annualized', key: 'Annualized', sortable: false },
      { title: 'Annualized 3-Yr', key: 'performance_three_year_annualized', sortable: false },
      { title: 'Annualized 5-Yr', key: 'performance_five_year_annualized', sortable: false },
      { title: 'Annualized 10-Yr', key: 'performance_ten_year_annualized', sortable: false },
      { title: 'Std Dev', key: 'Std Dev', sortable: false },
      { title: 'Sortino', key: 'Sortino', sortable: false }
    ],
    stocksTableHeaders: [
      { title: 'Stock Name', key: 'Stock Name', sortable: false },
      { title: '% of Portfolio', key: '% of Portfolio', sortable: false },
      { title: 'Unrealized Gain', key: 'Total Return', sortable: false },
      { title: 'Original Purchase Date', key: 'Original Purchase Date', sortable: false }
    ]
  }),

  computed: {
    isSubscriber() {
      return useUserStore().isSubscriber
    },
    ...mapStoreState({
      criteria: state => state.app.backtesterCriteria,
      user: state => state.user.user
    }),
    ...mapStoreState({
      limit: state => state.user.limit
    }),
    ...mapFields({
      criteria: 'app.backtesterCriteria'
    }),

    filteredTransactions() {
      const transactions = this.results.ending_period.transactions
      if (!transactions) return []
      return transactions
        .filter(t => !t.is_sold)
        .map(t => Object.defineProperty(t, 'rowType', { value: 'default' }))
    },

    backtestTableItems() {
      return [
        {
          name: this.hedged_matrix ? 'Unhedged Portfolio' : 'Whale(s)',
          YTD: last(this.starting_record.yearly_entries)[3] * 100,
          '1Y': this.GetYearlyPerformance(this.starting_record, 1),
          '2Y': this.GetYearlyPerformance(this.starting_record, 2),
          '3Y': this.GetYearlyPerformance(this.starting_record, 3),
          '5Y': this.GetYearlyPerformance(this.starting_record, 5),
          '7Y': this.GetYearlyPerformance(this.starting_record, 7),
          '10Y': this.GetYearlyPerformance(this.starting_record, 10),
          'Total Return': this.starting_record.performance * 100,
          Annualized: this.starting_record.annualized_performance * 100,
          performance_three_year_annualized:
            this.starting_record.performance_three_year_annualized * 100,
          performance_five_year_annualized:
            this.starting_record.performance_five_year_annualized * 100,
          performance_ten_year_annualized:
            this.starting_record.performance_ten_year_annualized * 100,
          'Std Dev': this.starting_record.standard_deviation * 100.0,
          Sortino: this.starting_record.sortino
        },
        // Conditionally insert the 'Hedged Portfolio' row
        ...(this.hedged_matrix
          ? [
              {
                name: 'Hedged Portfolio',
                YTD: last(this.hedged_matrix.yearly_entries)[3] * 100,
                '1Y': this.GetYearlyPerformance(this.hedged_matrix, 1),
                '2Y': this.GetYearlyPerformance(this.hedged_matrix, 2),
                '3Y': this.GetYearlyPerformance(this.hedged_matrix, 3),
                '5Y': this.GetYearlyPerformance(this.hedged_matrix, 5),
                '7Y': this.GetYearlyPerformance(this.hedged_matrix, 7),
                '10Y': this.GetYearlyPerformance(this.hedged_matrix, 10),
                'Total Return': this.hedged_matrix.performance * 100,
                Annualized: this.hedged_matrix.annualized_performance * 100,
                performance_three_year_annualized:
                  this.hedged_matrix.performance_three_year_annualized * 100,
                performance_five_year_annualized:
                  this.hedged_matrix.performance_five_year_annualized * 100,
                performance_ten_year_annualized:
                  this.hedged_matrix.performance_ten_year_annualized * 100,
                'Std Dev': this.hedged_matrix.standard_deviation * 100.0,
                Sortino: this.hedged_matrix.sortino
              }
            ]
          : []),
        {
          name: this.chart.comparisonIndex.description,
          YTD: last(this.chart.comparisonIndex.yearly_entries)[3] * 100,
          '1Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 1),
          '2Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 2),
          '3Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 3),
          '5Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 5),
          '7Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 7),
          '10Y': this.GetYearlyPerformance(this.chart.comparisonIndex, 10),
          'Total Return': this.chart.comparisonIndex.performance * 100,
          Annualized: this.chart.comparisonIndex.annualized_performance * 100,
          performance_three_year_annualized:
            this.chart.comparisonIndex.performance_three_year_annualized * 100,
          performance_five_year_annualized:
            this.chart.comparisonIndex.performance_five_year_annualized * 100,
          performance_ten_year_annualized:
            this.chart.comparisonIndex.performance_ten_year_annualized * 100,
          'Std Dev': this.chart.comparisonIndex.standard_deviation * 100.0,
          Sortino: this.chart.comparisonIndex.sortino
        }
      ]
    },

    stocksTableItems() {
      return [
        ...this.filteredTransactions,
        ...(this.results.ending_period.hedge ? [{ rowType: 'endingPeriodHedge' }] : []),
        ...(this.ending_hedged_transaction && !this.ending_hedged_transaction.is_sold
          ? [{ rowType: 'endingHedgedTransaction' }]
          : [])
      ]
    }
  },

  watch: {
    trackingStartDialog(isVisible) {
      if (isVisible) {
        this.$nextTick(() => this.$refs.backtestDescriptionField.focus())
      }
    }
  },

  methods: {
    genResults(results) {
      this.results = results
      this.indices = this.results.performance_matrices.slice(1) // slice removes 'Whales' entry

      if (this.results.performance_matrices.length > 0) {
        this.starting_record = this.results.performance_matrices[0]
      }
      if (this.results.performance_matrices.length > 1) {
        this.chart.comparisonIndex = this.results.performance_matrices[1]
      }
      if (last(this.results.performance_matrices).description === 'Hedged Portfolio') {
        this.hedged_matrix = last(this.results.performance_matrices)
        this.results.performance_matrices[0].description = 'Unhedged Portfolio'
        this.ending_hedged_transaction =
          last(this.hedged_matrix.monthly_entries).hedge_transaction || null

        const months = this.criteria.hedging_moving_average_months
        const found = this.moving_averages.some(ma => ma.months === months)

        if (!found) {
          this.moving_averages.push({
            description: `Past ${months} months Perf`,
            months
          })
        }
      } else this.hedged_matrix = null

      this.timeframe = this.timeframeItems[7]
      this.chart.xAxis = this.chartIntervalItems[2]

      this.$nextTick(() => {
        this.refresh()
        this.loadSectorChart('backtest_sector_chart', this.results.ending_period.transactions)
      })
    },

    refresh() {
      this.chartData = []
      let max = 0
      const timeframe = this.timeframe.time
      const today = new Date()
      let earliestDate = new Date(today.getFullYear(), today.getMonth() - timeframe)

      if (this.timeframe.value === 1) {
        earliestDate = new Date(today.getFullYear(), 0, 1)
      }

      const hedgingHighlight = {
        name: 'Hedging is on',
        color: '#FFFF66',
        area: true,
        data: [],
        tooltip: false,
        interactive: false,
        useInteractiveGuideline: false,
        lineinteractive: false
      } // https://djaodjin.com/blog/two-background-colors-nvd3-line-chart.blog.html
      let maxY = 0
      // or bar chart - create new entry see http://nvd3.org/examples/linePlusBar.html
      const a = []

      if (this.results.performance_matrices.length > 1) {
        a.push(this.results.performance_matrices[0])
        if (this.hedged_matrix) {
          a.push(this.hedged_matrix)
          a[0].description = 'Unhedged Portfolio'
        }

        a.push(this.chart.comparisonIndex)
      }
      if (this.chart.xAxis.value === 'monthly_entries') {
        const colors = ['#FFCC0F', '#C79F25', '#705A11', '#8BA9D0', '#6A90C1', '#066CA9', '#004B8C']
        for (let movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
          const ma = this.moving_averages[movingindex]
          ma.data = []
          ma.name = ma.description
          if (movingindex < colors.length) ma.color = colors[movingindex]
        }
      }
      for (let i = 0; i < a.length; i++) {
        const p = a[i]
        if (p.months) {
          continue
        }
        let data = []
        let tempdata = []
        let last = null
        if (this.chart.xAxis.value === 'quarterly_entries') {
          tempdata = p['monthly_entries']
        } else tempdata = p[this.chart.xAxis.value] // yearly_entries, monthly_entries, etc
        if (timeframe !== 0) {
          // strip out data earlier than earliestDate
          for (let j = 0; j < tempdata.length; j++) {
            const entry = tempdata[j]
            let darray = null
            if (entry.ending_balance) {
              darray = entry.end_date.split('-')
            } else {
              darray = entry[1].split('-')
            }
            const s = new Date(darray[0], darray[1] - 1, darray[2])
            if (s.getTime() >= earliestDate.getTime()) {
              data.push(entry)
            } else last = entry
          }
        } else {
          data = tempdata
        }
        if (this.hedged_matrix && i === 1) {
          tempdata = []
          data.forEach(hentry => {
            tempdata.push([
              hentry.end_date,
              hentry.end_date,
              hentry.ending_balance,
              hentry.perf_month,
              hentry.hedge_transaction
            ])
          })
          data = tempdata
        }
        const obj = {
          name: p.description,
          data: [],
          area: true
        }
        if (i === 0) {
          obj.color = '#57aea5' // '#96d8cc'
        } else if (i === 1 && this.hedged_matrix) {
          obj.color = '#00cc00'
        } else {
          obj.color = '#111'
        }
        let compound = 0
        let start = p.starting_balance
        if (timeframe !== 0) {
          if (last) start = last[2]
          else start = data[0][2]
        }
        for (let j = 0; j < data.length; j++) {
          const entry = data[j]
          const d3data = {
            x: null,
            y: null,
            hedged: false
          }
          let s = entry[0]
          if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
          else {
            const darray = s.split('-')
            s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
          }
          d3data.x = s.getTime()
          if (entry.length === 5 && entry[4]) {
            d3data.hedged = true
          }

          if (this.is_compound_chart) {
            compound = ((entry[2] - start) / start) * 1.0
            d3data.y = compound
          } else d3data.y = entry[3]
          obj.data.push(d3data)
          if (d3data.y > max) max = d3data.y
          if (d3data.y > maxY) maxY = d3data.y

          if (i === 0 && this.chart.xAxis.value === 'monthly_entries') {
            for (let movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
              const ma = this.moving_averages[movingindex]
              // ma will be obj with description and months, values
              // take average of current-ma.months and add to ma.values
              if (j >= ma.months) {
                // whale ma needs to be perf for past X months
                const base = data[j - ma.months][2]
                const maperf = (data[j][2] - base) / base
                // hedging comp ma needs to be avg of sums of perfs for past 12 monts
                // with base being mv from 12 months ago
                ma.data.push({
                  x: d3data.x,
                  y: maperf
                })
              } else {
                ma.data.push({
                  x: d3data.x,
                  y: null
                })
              }
            }
          }

          if (this.chart.xAxis.value === 'quarterly_entries') {
            j += 2
            if (j + 1 >= data.length) j = data.length - 1
          }
        }

        this.chartData.push(obj)
      }

      if (this.chart.xAxis.value === 'monthly_entries') {
        for (let movingindex = 0; movingindex < this.moving_averages.length; movingindex++) {
          const ma = this.moving_averages[movingindex]
          this.chartData.push(ma)
        }
        if (this.hedged_matrix && this.results.performance_matrices[0].moving_averages.length > 0) {
          const ma = {
            data: [],
            name: 'Hedging Moving Avg'
          }
          const hedgingMa = this.results.performance_matrices[0].moving_averages
          const compData = this.results.performance_matrices[0].monthly_entries
          const diff = compData.length - hedgingMa.length
          hedgingHighlight.data = []
          for (let i = 0; i < compData.length; i++) {
            const entry = compData[i]
            let s = entry[0]
            if (!isNaN(s) || s.length === 4) s = new Date(parseInt(s), 1, 1)
            else {
              const darray = s.split('-')
              s = new Date(darray[0], parseInt(darray[1]) - 1, darray[2])
            }
            const x = s.getTime()
            if (i >= diff) {
              const maentry = hedgingMa[i - diff]
              ma.data.push({
                x,
                y: maentry.value
              })
              const base = compData[i - diff][2]
              const entryMaPerf = (entry[2] - base) / base
              if (maentry.value > entryMaPerf) {
                hedgingHighlight.data.push({
                  x,
                  y: max
                })
              } else {
                hedgingHighlight.data.push({
                  x,
                  y: null
                })
              }
            } else {
              ma.data.push({
                x,
                y: null
              })
              hedgingHighlight.data.push({
                x,
                y: null
              })
            }
          }
          this.chartData.push(ma)
          this.chartData.push(hedgingHighlight)
          // modify for highcharts here
        }
      }

      Highcharts.chart('backtest_chart', {
        chart: {
          type: 'area',
          backgroundColor: '#FFFFFF'
        },
        title: {
          text: ''
        },
        subtitle: {},
        xAxis: {
          allowDecimals: false,
          labels: {
            formatter() {
              const d = new Date(this.value)
              if (this.chart.xAxis.value === 'yearly_entries') return d.getFullYear()
              else return d.getFullYear() + '-' + (d.getMonth() + 1) // d3.time.format('%Y-%m')(new Date(d));
              // return this.value; // clean, unformatted number for year
            }
          }
        },
        yAxis: {
          title: {
            text: ''
          },
          labels: {
            formatter() {
              return this.value * 100.0 + '%'
            }
          }
        },
        tooltip: {
          shared: true,
          formatter() {
            const points = this.points
            const pointsLength = points.length
            const d = new Date(points[0].key)

            let tooltipMarkup = pointsLength
              ? '<span style="font-size: 10px">' +
                d.getFullYear() +
                '-' +
                (d.getMonth() + 1) +
                '</span><br/>'
              : ''
            let index
            let yValueKwh

            for (index = 0; index < pointsLength; index += 1) {
              yValueKwh = (points[index].y * 100.0).toFixed(2)

              tooltipMarkup +=
                '<span style="color:' +
                points[index].series.color +
                '">\u25CF</span> ' +
                points[index].series.name +
                ': <b>' +
                yValueKwh +
                '%</b><br/>'
            }

            return tooltipMarkup
          }
        },
        credits: {
          enabled: false
        },
        plotOptions: {
          area: {
            // pointStart: 1940,
            marker: {
              enabled: false,
              symbol: 'circle',
              radius: 2,
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          }
        },
        series: this.chartData
      })
    },

    UpdateBacktestTracking(backtestTrackingId) {
      this.loadingTracking = true
      api
        .post('save_tracked_backtest_changes', this.criteria)
        .then(response => {
          useUserStore().loadTrackedBacktests()
          this.criteria.backtest_tracking_id = parseInt(response.data)
          this.trackingStartDialog = false
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .then(() => (this.loadingTracking = false))
    },

    trackingStart() {
      if (this.$refs.trackingForm.validate()) {
        this.loadingTracking = true
        this.criteria.backtest_tracking_id = null
        api
          .post('track_backtest', this.criteria)
          .then(response => {
            useUserStore().loadTrackedBacktests()
            this.criteria.backtest_tracking_id = parseInt(response.data)
            this.trackingStartDialog = false
          })
          .catch(error => {
            useAppStore().showNotification({
              type: 'error',
              text: (error.response && error.response.data) || error.message
            })
          })
          .then(() => (this.loadingTracking = false))
      }
    },

    cancelTrackingStart() {
      this.trackingStartDialog = false
      this.$refs.trackingForm.reset()
    },

    trackingStop() {
      this.loadingTracking = true

      api
        .get(`stop_tracking_backtest?id=${this.criteria.backtest_tracking_id}`)
        .then(response => {
          useUserStore().loadTrackedBacktests()
          this.criteria.backtest_tracking_id = null
          this.criteria.backtest_tracking_description = null
          this.trackingStopDialog = false
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .then(() => (this.loadingTracking = false))
    },

    loadSectorChart(element, data) {
      const chartSeries = []
      const series = {
        name: 'Sectors',
        data: []
      }
      const h = {}
      let totalMV = 0

      for (let i = 0; i < data.length; i++) {
        const stock = data[i]
        totalMV += stock.shares * stock.price_on_rebalancing_date
      }

      for (let i = 0; i < data.length; i++) {
        const stock = data[i]
        if (stock.shares === 0) continue
        if (!h[stock.sector]) {
          h[stock.sector] = {
            name: stock.sector,
            y: 0
          }
        }
        h[stock.sector].y += stock.shares * stock.price_on_rebalancing_date
      }

      for (const propertyName in h) {
        if (propertyName === undefined) continue
        const entry = h[propertyName]
        if (!entry.y) continue
        entry.y = (entry.y / totalMV) * 100.0
        series.data.push(entry)
      }

      chartSeries.push(series)

      Highcharts.chart(element, {
        chart: {
          type: 'pie',
          backgroundColor: '#FFFFFF'
        },
        legend: {
          itemStyle: {
            color: '#000000',
            fontWeight: 'bold'
          },
          itemHoverStyle: {
            color: '#333'
          }
        },
        title: {
          text: 'Sector Allocation'
        },
        xAxis: {
          type: 'category'
        },
        yAxis: {
          min: 0,
          title: {
            text: 'Sector Percentage'
          }
        },
        tooltip: {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>:  ({point.percentage:.0f}%)<br/>'
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: false
            },
            showInLegend: true
          }
        },
        credits: {
          enabled: false
        },
        series: chartSeries
      })
    },

    setTimeframe(timeframe) {
      this.timeframeProxy = timeframe.value
      this.refresh()
    },

    GetYearlyPerformance(record, numYears) {
      if (!record) return ''
      let perf = 0
      let start = 0
      let end = 0
      const numMonths = numYears * 12
      const months = record.monthly_entries
      if (months.length <= numMonths) return 'N/A'
      if (months[months.length - numMonths - 1].ending_balance) {
        start = months[months.length - numMonths - 1].ending_balance
        end = months[months.length - 1].ending_balance
      } else {
        start = months[months.length - numMonths - 1][2]
        end = months[months.length - 1][2]
      }
      perf = (end - start) / start
      return perf * 100.0
    },

    GetTransactionTotalReturn(transaction) {
      if (!transaction) return ''
      const basis = transaction.avg_price * transaction.shares
      const sp = transaction.current_stock_price
        ? transaction.current_stock_price
        : transaction.price_on_rebalancing_date
      const unrealizedGain = sp * transaction.shares - basis
      const gain = transaction.cumulative_gain_loss + unrealizedGain

      return (unrealizedGain / basis) * 100.0
    },

    AddMovingAverage() {
      if (!this.moving_averages.some(ma => ma.months === this.maToAdd)) {
        const sortedArray = this.moving_averages
        sortedArray.push({
          description: `${this.maToAdd} month moving average`,
          months: this.maToAdd
        })
        this.moving_averages = sortedArray.sort(
          (ma1, ma2) => parseInt(ma1.months) > parseInt(ma2.months)
        )
        this.maToAdd = null
      }
    },

    DeleteMovingAverage(ma) {
      const index = this.moving_averages.indexOf(ma)
      if (index >= 0) {
        this.moving_averages.splice(index, 1)
      }
    },

    CloseMovingAverageDialog() {
      this.movingAveragesDialog = false
      this.refresh()
    },
    addWatchlist(items) {
      //verify name and at least one stock
      if (this.newWatchlist.description.trim().length == 0 || items.length == 0) return
      this.saving = true
      for (var i = 0; i < items.length; i++) {
        if (this.newWatchlist.stocks.length >= this.limit) {
          break
        }
        this.newWatchlist.stocks.push({ id: items[i].stock_id })
      }
      let url = 'watchlists'
      api
        .post(url, this.newWatchlist)
        .then(response => {
          useUserStore().loadWatchlists()
          useAppStore().showNotification({ type: 'success', text: 'Watchlist has been saved' })
          this.newWatchlist = {
            id: null,
            description: 'New Watchlist',
            visibility_type_id: 0,
            stocks: []
          }
          this.CreateWatchlistDialog = false
          this.CreateWatchlistDialog2 = false
        })
        .catch(error => {
          useAppStore().showNotification({ type: 'error', text: error.message })
        })
        .then(() => (this.saving = false))
    }
  }
}
</script>

<style lang="scss">
#backtest-table,
#backtest-stocks-table {
  thead th {
    color: rgba(0, 0, 0, 0.87);
  }
}
</style>
